/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import StringEntry from "./StringEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import ReferenceEntry, { displayStringFromFHIRReference } from "./ReferenceEntry";
import UriEntry from "./UriEntry";
import RangeEntry, { displayStringFromFHIRRange } from "./RangeEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "./QuantityEntry";
import DatatypeSelector from "./DatatypeSelector";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const displayStringFromFHIRRelativeTime = (relativeTime) => {
  if (relativeTime === undefined || relativeTime === null || relativeTime === '') {
    return ""
  }
  if (typeof relativeTime === "object" && !Array.isArray(relativeTime)) {
    try {
      let relativeTimeDisplay = "";
      if (relativeTime.text) {
        relativeTimeDisplay = relativeTime.text;
      } else {
        if (relativeTime.offsetDuration) {
          relativeTimeDisplay = displayStringFromFHIRQuantity(relativeTime.offsetDuration) + " from ";
        }
        if (relativeTime.offsetRange) {
          relativeTimeDisplay = displayStringFromFHIRRange(relativeTime.offsetRange) + " from ";
        }
        if (relativeTime.contextCode) {
          relativeTimeDisplay += displayStringFromFHIRCodeableConcept(relativeTime.contextCode);
        }
        if (relativeTime.contextReference) {
          relativeTimeDisplay += displayStringFromFHIRReference(relativeTime.contextReference);
        }
        if (relativeTime.contextDefinition) {
          relativeTimeDisplay += "event defined at " + relativeTime.contextDefinition;
        }
        if (relativeTime.contextPath) {
          relativeTimeDisplay += " specific to " + relativeTime.contextPath;
        }
      }
      return relativeTimeDisplay;
    } catch (e) { console.log(e); }
  }
  if (typeof relativeTime === "string") {
    return `${relativeTime} [ERROR: RelativeTime data not in expected RelativeTime datatype.]`
  }
  return "[ERROR: RelativeTime data not interpretable.]";
}

const relativeTimeContextCodeCodeableConceptLevelValueSet = [
  {
    "coding": [
      {
        "system": "http://snomed.info/sct",
        "code": "709491003",
        "display": "Enrollment in clinical trial (procedure)"
      }
    ]
  },
  {
    "coding": [
      {
        "system": "http://snomed.info/sct",
        "code": "450332002",
        "display": "Assessment of eligibility for clinical trial (procedure)"
      }
    ]
  },
  {
    "coding": [
      {
        "system": "http://snomed.info/sct",
        "code": "32485007",
        "display": "Hospital admission (procedure)"
      }
    ]
  },
  {
    "coding": [
      {
        "system": "http://snomed.info/sct",
        "code": "308283009",
        "display": "Discharge from hospital (procedure)"
      }
    ]
  }
];

const RelativeTimeEntry = ({ elementName, fieldLabel, startingValue, setResourceState, valueSet,
  inTableCell, startCollapsed, containedResourceDictionary = {} }) => {

  const [relativeTimeState, setRelativeTimeState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
  const [relativeTimeOffsetDatatypeState, setRelativeTimeOffsetDatatypeState] = useState('none');

  const relativeTimeOffsetAllowedDatatypes = ['Duration', 'Range'];

  useEffect(() => {
    let startingRelativeTime = {
      "contextReference": "", "contextDefinition": "", "contextPath": "", "contextCode": "",
      "offsetDuration": "", "offsetRange": "", "text": ""
    };
    let startingRelativeTimeOffsetDatatype = 'none';
    if (startingValue) {
      if (startingValue.extension) { startingRelativeTime.extension = startingValue.extension; }
      if (startingValue.contextReference) { startingRelativeTime.contextReference = startingValue.contextReference; }
      if (startingValue.contextDefinition) { startingRelativeTime.contextDefinition = startingValue.contextDefinition; }
      if (startingValue.contextPath) { startingRelativeTime.contextPath = startingValue.contextPath; }
      if (startingValue.contextCode) { startingRelativeTime.contextCode = startingValue.contextCode; }
      if (startingValue.offsetDuration) {
        startingRelativeTime.offsetDuration = startingValue.offsetDuration;
        startingRelativeTimeOffsetDatatype = "Duration";
      }
      if (startingValue.offsetRange) {
        startingRelativeTime.offsetRange = startingValue.offsetRange;
        startingRelativeTimeOffsetDatatype = "Range";
      }
      if (startingValue.text) { startingRelativeTime.text = startingValue.text; }
    }
    setRelativeTimeState(JSON.parse(JSON.stringify(startingRelativeTime)));
    setRelativeTimeOffsetDatatypeState(startingRelativeTimeOffsetDatatype);
  }, []);

  useEffect((() => {
    if (Object.keys(relativeTimeState).length) {
      let newRelativeTime = {};
      if (relativeTimeState.extension) { newRelativeTime.extension = relativeTimeState.extension; }
      if (relativeTimeState.contextReference) { newRelativeTime.contextReference = relativeTimeState.contextReference; }
      if (relativeTimeState.contextDefinition) { newRelativeTime.contextDefinition = relativeTimeState.contextDefinition; }
      if (relativeTimeState.contextPath) { newRelativeTime.contextPath = relativeTimeState.contextPath; }
      if (relativeTimeState.contextCode) { newRelativeTime.contextCode = relativeTimeState.contextCode; }
      if (relativeTimeOffsetDatatypeState === "Duration" && relativeTimeState.offsetDuration) { newRelativeTime.offsetDuration = relativeTimeState.offsetDuration; }
      if (relativeTimeOffsetDatatypeState === "Range" && relativeTimeState.offsetRange) { newRelativeTime.offsetRange = relativeTimeState.offsetRange; }
      if (relativeTimeState.text) { newRelativeTime.text = relativeTimeState.text; }
      if (Object.keys(newRelativeTime).length === 0) {
        newRelativeTime = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newRelativeTime }; });
    }
  }), [relativeTimeState, relativeTimeOffsetDatatypeState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRRelativeTime(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else if (Object.keys(relativeTimeState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <p style={{ marginBottom: "0px" }}><b>Text Summary: </b> (brief description for display instead of structured data)</p>
        <StringEntry elementName='text' setResourceState={setRelativeTimeState}
          fieldLabel={'Display of ' + fieldLabel} startingValue={relativeTimeState.text} />
        <br />
        <p style={{ marginBottom: "0px" }}><b>The Event or Context (Reference Point in Time):</b></p>
        <CodeableConceptEntry elementName='contextCode' fieldLabel='Event (as CodeableConcept)'
          valueSet={valueSet} startCollapsed={startCollapsed}
          codeableConceptLevelValueSet={relativeTimeContextCodeCodeableConceptLevelValueSet}
          startingValue={relativeTimeState.contextCode} setResourceState={setRelativeTimeState} />
        <ReferenceEntry elementName='contextReference' setResourceState={setRelativeTimeState}
          startCollapsed={startCollapsed} enableCreation={true}
          containedResourceDictionary={containedResourceDictionary}
          fieldLabel={'Event (as Resource Reference)'} startingValue={relativeTimeState.contextReference} />
        <UriEntry elementName='contextDefinition' setResourceState={setRelativeTimeState}
          fieldLabel={'Event (as Canonical URI instead of Resource Reference)'}
          startingValue={relativeTimeState.contextDefinition} />
        <StringEntry elementName='contextPath' fieldLabel='Path (within Resource Reference)'
          startingValue={relativeTimeState.contextPath} setResourceState={setRelativeTimeState} />
        <p style={{ marginBottom: "0px" }}><b>Offset:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='offset[x]' allowedDatatypes={relativeTimeOffsetAllowedDatatypes}
            datatypeState={relativeTimeOffsetDatatypeState} setDatatypeState={setRelativeTimeOffsetDatatypeState} inTableCell={inTableCell} />
          {relativeTimeOffsetDatatypeState === "Duration" &&
            <QuantityEntry elementName='offsetDuration' fieldLabel="Offset Duration"
              startingValue={relativeTimeState.offsetDuration} setResourceState={setRelativeTimeState}
              dataEntryStyle={"Duration"} inTableCell={inTableCell} startCollapsed={false} />}
          {relativeTimeOffsetDatatypeState === "Range" &&
            <RangeEntry elementName='offsetRange' setResourceState={setRelativeTimeState}
              startCollapsed={false} inTableCell={inTableCell} dataEntryStyle={"Duration"}
              fieldLabel="Offset Range" startingValue={relativeTimeState.offsetRange} />
          }
        </div>
      </div>
    </>
  }
}

export default RelativeTimeEntry;
export { displayStringFromFHIRRelativeTime };